import { Brand } from '@/components/Brand';
import { Button } from '@/components/ui/button';
import { LanguageSwitcher } from '@/features/i18n/LanguageSwitcher';
import { ThemeToggle } from '@/features/theme/ThemeToggle';
import { Link, createFileRoute } from '@tanstack/react-router';
import { ArrowRight, FileSpreadsheet, FileText, Mail, ShieldCheck, Sparkles } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/')({
  component: LandingPage,
});

function LandingPage() {
  const { t } = useTranslation();
  return (
    <div className="relative min-h-screen overflow-hidden bg-background">
      <div
        className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[800px] bg-aurora opacity-80"
        aria-hidden
      />
      <div className="pointer-events-none absolute inset-0 -z-10 bg-grid opacity-30" aria-hidden />

      <header className="relative z-10 flex items-center justify-between gap-2 px-6 py-5 lg:px-10">
        <Link to="/">
          <Brand size="sm" />
        </Link>
        <div className="flex items-center gap-1.5">
          <LanguageSwitcher />
          <ThemeToggle />
          <div className="mx-2 h-6 w-px bg-border/60" aria-hidden />
          <Link to="/login">
            <Button variant="ghost" size="sm">
              {t('landing.cta.login')}
            </Button>
          </Link>
          <Link to="/register">
            <Button size="sm" className="bg-foreground text-background hover:opacity-95">
              {t('landing.cta.register')}
            </Button>
          </Link>
        </div>
      </header>

      <main className="relative z-10 mx-auto max-w-6xl px-6 pb-24 pt-16 lg:pt-24">
        <div className="mx-auto max-w-3xl text-center">
          <div className="mx-auto inline-flex items-center gap-2 rounded-full border border-border/70 bg-card/60 px-3.5 py-1 text-[12px] font-medium text-muted-foreground backdrop-blur">
            <Sparkles className="size-3.5 text-primary" aria-hidden />
            <span>{t('landing.eyebrow')}</span>
          </div>
          <h1 className="mt-6 text-balance text-5xl font-semibold leading-tight tracking-tight text-foreground sm:text-6xl">
            {t('landing.title')}
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-pretty text-lg text-muted-foreground">
            {t('landing.lede')}
          </p>
          <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
            <Link to="/register">
              <Button
                size="lg"
                className="group h-11 gap-2 bg-foreground text-background shadow-elevate hover:opacity-95"
              >
                {t('landing.cta.register')}
                <ArrowRight
                  className="size-4 transition-transform group-hover:translate-x-0.5"
                  aria-hidden
                />
              </Button>
            </Link>
            <Link to="/login">
              <Button size="lg" variant="outline" className="h-11">
                {t('landing.cta.login')}
              </Button>
            </Link>
          </div>
        </div>

        <section className="mt-20 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Feature
            icon={<FileText className="size-5" aria-hidden />}
            title={t('landing.features.docs.title')}
            body={t('landing.features.docs.body')}
          />
          <Feature
            icon={<FileSpreadsheet className="size-5" aria-hidden />}
            title={t('landing.features.sheets.title')}
            body={t('landing.features.sheets.body')}
          />
          <Feature
            icon={<Mail className="size-5" aria-hidden />}
            title={t('landing.features.mail.title')}
            body={t('landing.features.mail.body')}
          />
          <Feature
            icon={<ShieldCheck className="size-5" aria-hidden />}
            title={t('landing.features.api.title')}
            body={t('landing.features.api.body')}
          />
        </section>
      </main>
    </div>
  );
}

function Feature({ icon, title, body }: { icon: ReactNode; title: string; body: string }) {
  return (
    <article className="group relative overflow-hidden rounded-xl border border-border/70 bg-card/80 p-5 shadow-soft backdrop-blur transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-elevate">
      <div className="flex size-10 items-center justify-center rounded-lg bg-primary/10 text-primary transition-transform group-hover:scale-105">
        {icon}
      </div>
      <h2 className="mt-4 text-sm font-semibold tracking-tight">{title}</h2>
      <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{body}</p>
    </article>
  );
}
